import { Grid, Box, Collapse, IconButton, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";

/**CONSTANT */
import { Icons, icons, Imgs } from "constants/images";
import { colors } from 'constants/theme';

/**Component */
import CollapseContainer from 'component/Collapse/container';
/**STYLE */
import '../../styles/AnnouncementContent.css';
import * as UseStyle from './UseStyle';
import { fetchActiveAnnouncements, fetchAnnouncementHistory } from 'features/setting/announcement/announcement.utils';
import type { AnnouncementItem } from 'features/setting/announcement/announcement.utils';
import { fetchActiveSystemUpdates } from 'features/setting/system-updates.utils';
import type { SystemUpdateItem, SystemUpdateSection } from 'features/setting/system-updates.utils';

import ManualNav from './ManualNav';
import ManualContent from './ManualContent';
import ManualApi from 'api/manual.api';
import type { ManualPage } from './manuals/manual.types';

function formatUpdateDateLabel(d: string, startTime?: string, endTime?: string) {
    try {
        const date = new Date(d)
        const th = date.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })
        const time = startTime && endTime ? ` (${startTime}–${endTime})` : ''
        return `อัปเดตประจำวันที่ ${th}${time}`
    } catch {
        return d || 'อัปเดตระบบ'
    }
}

/** หัวข้อแสดงสำหรับรายการอัปเดต: ใช้หัวข้อหลักจาก API ถ้ามี ไม่ก็ใช้วันที่ */
function getUpdateTitle(item: SystemUpdateItem) {
    const t = item.mainTitle?.trim()
    return t || formatUpdateDateLabel(item.updateDate, item.startTime, item.endTime)
}

/** ป้องกัน sections เป็น string จาก API ให้เป็น array เสมอ */
function getSections(item: SystemUpdateItem): SystemUpdateSection[] {
    const s = item.sections
    if (Array.isArray(s)) return s
    if (typeof s === 'string') {
        try {
            const p = JSON.parse(s) as SystemUpdateSection[] | null
            return Array.isArray(p) ? p : []
        } catch {
            return []
        }
    }
    return []
}

/** สไตล์หัวข้อพื้นเขียว (ใช้กับ NEW FEATURE / FUNCTION UPDATES และหัวข้อแต่ละ section) */
const sectionTitleBarSx = {
    background: colors.themeSecondColor10 ?? 'rgba(28, 185, 154, 0.1)',
    color: '#1a1a1a',
    padding: '10px 14px',
    borderRadius: 1,
    margin: '12px 0 8px 0',
    fontWeight: 600,
    fontSize: 15,
}

export default function UpdateSidebar() {
    const { t } = useTranslation();
    const [updatepage, setUpdatepage] = useState<string>("default");
    const [collapse, setCollapse] = useState<boolean>(false);
    const [menu, setMenu] = useState<"Update" | "Manual">("Update")
    const [selectedManualId, setSelectedManualId] = useState<string | null>(null)
    const [loadedManual, setLoadedManual] = useState<ManualPage | null>(null)
    const [manualLoading, setManualLoading] = useState(false)
    const [systemAnnouncements, setSystemAnnouncements] = useState<AnnouncementItem[]>([])
    const [pastAnnouncements, setPastAnnouncements] = useState<AnnouncementItem[]>([])
    const [apiUpdates, setApiUpdates] = useState<SystemUpdateItem[]>([])
    const [readStateApi, setReadStateApi] = useState<Record<number, { feature?: boolean; update?: boolean }>>({})
    const [openFeatureToggles, setOpenFeatureToggles] = useState<Record<string, boolean>>({})
    const [openUpdateToggles, setOpenUpdateToggles] = useState<Record<string, boolean>>({})

    useEffect(() => {
        fetchActiveAnnouncements().then((list) => {
            const fromApi = (list || []).filter((a) => a.type === 'SYSTEM_UPDATE')
            setSystemAnnouncements(fromApi)
        })
    }, [])

    useEffect(() => {
        fetchAnnouncementHistory({ type: 'SYSTEM_UPDATE' }).then((list) => {
            const history = (list || []).filter((a) => a.type === 'SYSTEM_UPDATE')
            setPastAnnouncements(history)
        })
    }, [])

    useEffect(() => {
        fetchActiveSystemUpdates().then((list) => setApiUpdates(list || []))
    }, [])

    useEffect(() => {
        if (!selectedManualId) {
            setLoadedManual(null)
            return
        }
        let cancelled = false
        setManualLoading(true)
        ManualApi.getManualById(selectedManualId)
            .then((manual) => {
                if (!cancelled) setLoadedManual(manual)
            })
            .finally(() => {
                if (!cancelled) setManualLoading(false)
            })
        return () => { cancelled = true }
    }, [selectedManualId])

    const setReadApi = (updateId: number, type: 'feature' | 'update', val: boolean) => {
        setReadStateApi((prev) => ({
            ...prev,
            [updateId]: { ...prev[updateId], [type]: val },
        }))
    }

    return (
        <div className='h-100 w-100 overflow-hidden'>
            <div className='h-100 w-100 d-flex flex-column'>
            <UseStyle.HeaderUpdatePage>
                <p>{t('UPDATEPAGE.HEAD.UPDATE_LIST_AND_MANUAL')}</p>

                <UseStyle.ButtonGroup>
                    <UseStyle.UpdateListButton onClick={() => setMenu("Update")}
                        style={{
                            backgroundColor: `${menu === "Update" ? colors.white : colors.themeSecondColor}`,
                            color: `${menu === "Update" ? colors.themeSecondColor : colors.white}`
                        }}
                    >
                        {menu === "Update" ? <span><Icons.Gear /></span> : <img src={icons.white_gear} alt="updateicon_list" />}
                        {t('UPDATEPAGE.BUTTON.UPDATE_LIST')}
                    </UseStyle.UpdateListButton>
                    <UseStyle.ManualButton onClick={() => setMenu("Manual")}
                        style={{
                            backgroundColor: `${menu === "Manual" ? colors.white : colors.themeSecondColor}`,
                            color: `${menu === "Manual" ? colors.themeSecondColor : colors.white}`
                        }}
                    >
                        {menu === "Manual" ? <span><Icons.Manual /></span> : <img src={icons.white_manual} alt="manual" />}
                        {t('UPDATEPAGE.BUTTON.MANUAL')}
                    </UseStyle.ManualButton>
                </UseStyle.ButtonGroup>
            </UseStyle.HeaderUpdatePage>
            <div style={{ flex: 1, minHeight: 0 }}>
            {menu === "Update" && <CollapseContainer
                tooltipExpand={t('UPDATEPAGE.TOOLTIP.EXPAND_MENU')}
                tooltipShorten={t('UPDATEPAGE.TOOLTIP.SHORTEN_MENU')}
                styleComponentLeft={{ height: '100%', overflowY: 'auto' }}
                styleComponentRight={{ height: '100%', overflowY: 'auto' }}
                callBackStatusCollapse={() => setCollapse(!collapse)}
                componentLeft={
                    <div>
                        {systemAnnouncements.length > 0 && (
                            <Grid item className='col-12' style={{ marginBottom: 16 }}>
                                <UseStyle.TextHeader>
                                    ประกาศจากระบบ
                                </UseStyle.TextHeader>
                                {systemAnnouncements.map((ann) => (
                                    <UseStyle.SlideDataContainer key={ann.announcementId} defaultExpanded={false}>
                                        <UseStyle.SlideData aria-controls="panel-ann-content">
                                            <div
                                                className="d-flex flex-row justify-content-start align-items-center cursor-pointer"
                                                onClick={() => setUpdatepage('announcement-' + ann.announcementId)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => e.key === 'Enter' && setUpdatepage('announcement-' + ann.announcementId)}
                                            >
                                                <span className="pr-2"><Icons.Gear /></span>
                                                <span style={{ color: updatepage === 'announcement-' + ann.announcementId ? colors.themeSecondColor : colors.black }}>
                                                    {ann.title}
                                                </span>
                                            </div>
                                        </UseStyle.SlideData>
                                    </UseStyle.SlideDataContainer>
                                ))}
                            </Grid>
                        )}
                        <Grid item className='col-12' style={{ marginBottom: 16 }}>
                                <UseStyle.TextHeader>
                                    ดูย้อนหลังข่าวประกาศ
                                </UseStyle.TextHeader>
                                {pastAnnouncements
                                    .filter((ann) => !systemAnnouncements.some((s) => s.announcementId === ann.announcementId))
                                    .length === 0 ? (
                                    <Box sx={{ py: 1, px: 1, color: 'text.secondary', fontSize: 14 }}>
                                        ไม่มีรายการข่าวประกาศย้อนหลัง
                                    </Box>
                                ) : (
                                    pastAnnouncements
                                        .filter((ann) => !systemAnnouncements.some((s) => s.announcementId === ann.announcementId))
                                        .map((ann) => (
                                            <UseStyle.SlideDataContainer key={'past-' + ann.announcementId} defaultExpanded={false}>
                                                <UseStyle.SlideData aria-controls="panel-past-ann-content">
                                                    <div
                                                        className="d-flex flex-row justify-content-start align-items-center cursor-pointer"
                                                        onClick={() => setUpdatepage('history-' + ann.announcementId)}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => e.key === 'Enter' && setUpdatepage('history-' + ann.announcementId)}
                                                    >
                                                        <span className="pr-2"><Icons.Gear /></span>
                                                        <span style={{ color: updatepage === 'history-' + ann.announcementId ? colors.themeSecondColor : colors.black }}>
                                                            {ann.title}
                                                        </span>
                                                    </div>
                                                </UseStyle.SlideData>
                                            </UseStyle.SlideDataContainer>
                                        ))
                                )}
                            </Grid>
                        <Grid item className='col-12'>
                            <UseStyle.TextHeader>
                                {t('UPDATEPAGE.HEAD.UPDATE_LIST')}
                            </UseStyle.TextHeader>
                            {apiUpdates.length === 0 && (
                                <Box sx={{ py: 2, px: 1, color: 'text.secondary', fontSize: 14 }}>
                                    {t('UPDATEPAGE.DETAIL.NO_HAVE_NOTI_DETAIL') || 'ไม่มีรายการอัปเดตในขณะนี้'}
                                </Box>
                            )}
                            {apiUpdates.map((item) => {
                                const read = readStateApi[item.updateId] || {}
                                const allRead = read.feature && read.update
                                const dateLabel = getUpdateTitle(item)
                                const sections = getSections(item)
                                const featureSections = sections.filter((s) => s.type === 'NEW_FEATURE' && s.content?.trim())
                                const hasFeature = (featureSections.length > 0) || !!(item.newFeatures && item.newFeatures.trim())
                                return (
                                    <UseStyle.SlideDataContainer key={item.updateId} defaultExpanded={false}>
                                        <UseStyle.SlideData aria-controls="panel2d-content" color={allRead ? colors.textLightGray : colors.black}>
                                            <div className='d-flex flex-row justify-content-start align-items-center'>
                                                {allRead ? <img src={icons.black_gear} alt='read' className='pr-2' /> : <span className='pr-2'><Icons.Gear /> </span>}
                                                <span style={{ color: allRead ? colors.textLightGray : colors.black }}>{dateLabel}</span>
                                            </div>
                                        </UseStyle.SlideData>
                                        <UseStyle.ListUpdate>
                                            <Grid container spacing={0}>
                                                {hasFeature && (
                                                    <Grid item className='col-12'>
                                                        <div
                                                            className="d-flex flex-row pl-3 pt-1 cursor-pointer"
                                                            onClick={() => { setUpdatepage('api-' + item.updateId + '-feature'); setReadApi(item.updateId, 'feature', true); }}
                                                        >
                                                            {read.feature ? <img src={icons.black_gear} alt="" /> : <span><Icons.Gear /> </span>}
                                                            <span style={{ fontSize: 16, fontWeight: 400, color: read.feature ? colors.black : colors.themeSecondColor, padding: '0 8px' }}>{t('UPDATEPAGE.SUB_DETAIL.NEW_FEATURE')}</span>
                                                            {read.feature ? <img src={icons.black_new} alt="" /> : <span><Icons.New /></span>}
                                                        </div>
                                                    </Grid>
                                                )}
                                                <Grid item className='col-12'>
                                                    <div
                                                        className="d-flex flex-row pl-3 pt-1 cursor-pointer"
                                                        onClick={() => { setUpdatepage('api-' + item.updateId + '-update'); setReadApi(item.updateId, 'update', true); }}
                                                    >
                                                        {read.update ? <img src={icons.black_re} alt="" /> : <span><Icons.NewUpdate /></span>}
                                                        <span style={{ fontSize: 16, fontWeight: 400, color: read.update ? colors.black : colors.themeSecondColor, padding: '0 8px' }}>{t('UPDATEPAGE.SUB_DETAIL.UPDATE')}</span>
                                                        {read.update ? <img src={icons.black_new} alt="" /> : <span><Icons.New /></span>}
                                                    </div>
                                                </Grid>
                                            </Grid>
                                        </UseStyle.ListUpdate>
                                    </UseStyle.SlideDataContainer>
                                )
                            })}
                        </Grid>
                    </div>
                }
                componentRight={
                    <div style={{ padding: '0 16px' }}>
                        {updatepage.startsWith('announcement-') && (() => {
                            const id = parseInt(updatepage.replace('announcement-', ''), 10)
                            const ann = systemAnnouncements.find((a) => a.announcementId === id)
                            if (!ann) return null
                            return (
                                <div>
                                    <UseStyle.ShowContent>
                                        <UseStyle.HeaderDetail className="mb-2">{ann.title}</UseStyle.HeaderDetail>
                                        <Box
                                            className="w-100 mt-2 announcement-html-content"
                                            dangerouslySetInnerHTML={{ __html: ann.content || '' }}
                                        />
                                    </UseStyle.ShowContent>
                                </div>
                            )
                        })()}
                        {updatepage.startsWith('history-') && (() => {
                            const id = parseInt(updatepage.replace('history-', ''), 10)
                            const ann = pastAnnouncements.find((a) => a.announcementId === id)
                            if (!ann) return null
                            return (
                                <div>
                                    <UseStyle.ShowContent>
                                        <UseStyle.HeaderDetail className="mb-2">{ann.title}</UseStyle.HeaderDetail>
                                        <Box
                                            className="w-100 mt-2 announcement-html-content"
                                            dangerouslySetInnerHTML={{ __html: ann.content || '' }}
                                        />
                                    </UseStyle.ShowContent>
                                </div>
                            )
                        })()}
                        {apiUpdates.map((item) => {
                            if (updatepage !== 'api-' + item.updateId + '-update') return null
                            const sections = getSections(item)
                            const updateBlockSx = {
                                fontSize: 14,
                                lineHeight: 1.8,
                                '& h2, & h3, & h4': {
                                    background: colors.themeSecondColor10 ?? 'rgba(28, 185, 154, 0.1)',
                                    color: '#1a1a1a',
                                    padding: '10px 14px',
                                    borderRadius: 1,
                                    margin: '12px 0 8px 0',
                                    fontWeight: 600,
                                    fontSize: 15,
                                },
                                '& ul, & ol': { margin: '6px 0 12px 0', paddingLeft: '24px !important', '& li': { marginBottom: 4 } },
                                '& ul li,& ol li': { marginBottom: '4px !important' },
                                '& p': { marginBottom: 8 },
                                '& strong': { fontWeight: 600 },
                            }
                            const functionUpdateSections = sections.filter((s) => s.type === 'FUNCTION_UPDATE')
                            const bugFixSections = sections.filter((s) => s.type === 'BUG_FIXES')
                            const hasTypedSections = functionUpdateSections.length > 0 || bugFixSections.length > 0
                            const hasImprovements = item.improvements && item.improvements.trim()
                            const hasBugFixes = item.bugFixes && item.bugFixes.trim()
                            const hasLegacySections = !!(sections.length > 0 && !sections.some((s) => s.type === 'BUG_FIXES' || s.type === 'FUNCTION_UPDATE'))
                            const legacyUpdateSections = hasLegacySections ? sections.slice(1) : []
                            const renderSectionWithToggle = (sec: typeof sections[0], toggleKey: string, isLastInGroup: boolean) => (
                                <Box key={toggleKey} sx={{ mb: isLastInGroup ? 0 : 2 }}>
                                    {sec.title && (
                                        <Box
                                            component="div"
                                            onClick={() => setOpenUpdateToggles((prev) => ({ ...prev, [toggleKey]: !prev[toggleKey] }))}
                                            sx={{
                                                ...sectionTitleBarSx,
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                            }}
                                        >
                                            <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                                                <ExpandMoreIcon sx={{ transform: openUpdateToggles[toggleKey] === true ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: 20 }} />
                                            </IconButton>
                                            {sec.title}
                                        </Box>
                                    )}
                                    <Collapse in={openUpdateToggles[toggleKey] === true} timeout="auto" unmountOnExit>
                                        <Box sx={updateBlockSx} dangerouslySetInnerHTML={{ __html: sec.content || '' }} />
                                    </Collapse>
                                </Box>
                            )
                            return (
                                <div key={item.updateId}>
                                    <UseStyle.ShowContent>
                                        <div className="w-100 mt-2">
                                            {hasTypedSections ? (
                                                <>
                                                    {functionUpdateSections.length > 0 && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <UseStyle.HeaderDetail className="mb-2">FUNCTION UPDATES</UseStyle.HeaderDetail>

                                                            {functionUpdateSections.map((sec, i) =>
                                                                renderSectionWithToggle(
                                                                    sec,
                                                                    `update-${item.updateId}-FUNCTION_UPDATE-${i}`,
                                                                    i === functionUpdateSections.length - 1
                                                                )
                                                            )}
                                                        </Box>
                                                    )}
                                                    {bugFixSections.length > 0 && (
                                                        <Box sx={{ mt: functionUpdateSections.length > 0 ? 2 : 0 }}>
                                                            <UseStyle.HeaderDetail className="mb-2">BUG FIXES</UseStyle.HeaderDetail>
                                                            {bugFixSections.map((sec, i) =>
                                                                renderSectionWithToggle(
                                                                    sec,
                                                                    `update-${item.updateId}-BUG_FIXES-${i}`,
                                                                    i === bugFixSections.length - 1
                                                                )
                                                            )}
                                                        </Box>
                                                    )}
                                                </>
                                            ) : legacyUpdateSections.length > 0 ? (
                                                legacyUpdateSections.map((sec, i) => {
                                                    const toggleKey = `legacy-${item.updateId}-${i}`
                                                    const isOpen = openUpdateToggles[toggleKey] === true
                                                    return (
                                                        <Box key={i} sx={{ mb: i < legacyUpdateSections.length - 1 ? 2 : 0 }}>
                                                            {sec.title && (
                                                                <Box
                                                                    component="div"
                                                                    onClick={() => setOpenUpdateToggles((prev) => ({ ...prev, [toggleKey]: !prev[toggleKey] }))}
                                                                    sx={{
                                                                        ...sectionTitleBarSx,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none',
                                                                    }}
                                                                >
                                                                    <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                                                                        <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: 20 }} />
                                                                    </IconButton>
                                                                    {sec.title}
                                                                </Box>
                                                            )}
                                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                <Box sx={updateBlockSx} dangerouslySetInnerHTML={{ __html: sec.content || '' }} />
                                                            </Collapse>
                                                        </Box>
                                                    )
                                                })
                                            ) : (
                                                <>
                                                    {hasImprovements && (() => {
                                                        const toggleKey = `improvements-${item.updateId}`
                                                        const isOpen = openUpdateToggles[toggleKey] === true
                                                        return (
                                                            <Box sx={{ mb: 2 }}>
                                                                <Box
                                                                    component="div"
                                                                    onClick={() => setOpenUpdateToggles((prev) => ({ ...prev, [toggleKey]: !prev[toggleKey] }))}
                                                                    sx={{
                                                                        ...sectionTitleBarSx,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none',
                                                                    }}
                                                                >
                                                                    <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                                                                        <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: 20 }} />
                                                                    </IconButton>
                                                                    {t('UPDATEPAGE.SUB_DETAIL.UPDATE')}
                                                                </Box>
                                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                    <Box sx={updateBlockSx} dangerouslySetInnerHTML={{ __html: item.improvements! }} />
                                                                </Collapse>
                                                            </Box>
                                                        )
                                                    })()}
                                                    {hasBugFixes && (() => {
                                                        const toggleKey = `bugfixes-${item.updateId}`
                                                        const isOpen = openUpdateToggles[toggleKey] === true
                                                        return (
                                                            <Box>
                                                                <Box
                                                                    component="div"
                                                                    onClick={() => setOpenUpdateToggles((prev) => ({ ...prev, [toggleKey]: !prev[toggleKey] }))}
                                                                    sx={{
                                                                        ...sectionTitleBarSx,
                                                                        mt: hasImprovements ? 2 : 0,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none',
                                                                    }}
                                                                >
                                                                    <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                                                                        <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: 20 }} />
                                                                    </IconButton>
                                                                    ฟังก์ชันที่ได้รับการแก้ไข
                                                                </Box>
                                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                    <Box sx={updateBlockSx} dangerouslySetInnerHTML={{ __html: item.bugFixes! }} />
                                                                </Collapse>
                                                            </Box>
                                                        )
                                                    })()}
                                                    {!hasImprovements && !hasBugFixes && <span style={{ color: '#999' }}>ไม่มีเนื้อหา</span>}
                                                </>
                                            )}
                                        </div>
                                    </UseStyle.ShowContent>
                                </div>
                            )
                        })}

                        {apiUpdates.map((item) => {
                            if (updatepage !== 'api-' + item.updateId + '-feature') return null
                            const sections = getSections(item)
                            const featureSections = sections.filter((s) => s.type === 'NEW_FEATURE')
                            const hasTypedFeatures = featureSections && featureSections.length > 0
                            const newFeatureBlockSx = {
                                fontSize: 14,
                                lineHeight: 1.8,
                                '& h2, & h3, & h4': {
                                    background: colors.themeSecondColor10 ?? 'rgba(28, 185, 154, 0.1)',
                                    color: '#1a1a1a',
                                    padding: '10px 14px',
                                    borderRadius: 1,
                                    margin: '12px 0 8px 0',
                                    fontWeight: 600,
                                    fontSize: 15,
                                },
                                '& ul, & ol': { margin: '6px 0 12px 0', paddingLeft: '24px !important', '& li': { marginBottom: 4 } },
                                '& ul li,& ol li, & p': { marginBottom: '4px !important' },
                                '& p': { marginBottom: 8 },
                                '& strong': { fontWeight: 600 },
                            }
                            const legacyFirstSection = sections[0] && !sections.some((s) => s.type === 'NEW_FEATURE' || s.type === 'FUNCTION_UPDATE')

                            const html = item.newFeatures && item.newFeatures.trim() ? item.newFeatures : (legacyFirstSection ? (sections[0].content || '') : '') || '<span style="color:#999">ไม่มีเนื้อหา</span>'
                            return (
                                <div key={item.updateId}>
                                    <UseStyle.ShowContent>
                                        <UseStyle.HeaderDetail className="mb-2">{ 'NEW FEATURE'}</UseStyle.HeaderDetail>
                                        {hasTypedFeatures ? (
                                            featureSections!.map((sec, i) => {
                                                const toggleKey = `feature-${item.updateId}-${i}`
                                                const isOpen = openFeatureToggles[toggleKey] === true
                                                return (
                                                    <Box key={i} sx={{ mb: i < featureSections!.length - 1 ? 2 : 0 }}>
                                                        {sec.title && (
                                                            <Box
                                                                component="div"
                                                                onClick={() => setOpenFeatureToggles((prev) => ({ ...prev, [toggleKey]: !prev[toggleKey] }))}
                                                                sx={{
                                                                    ...sectionTitleBarSx,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    cursor: 'pointer',
                                                                    userSelect: 'none',
                                                                }}
                                                            >
                                                                <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                                                                    <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: 20 }} />
                                                                </IconButton>
                                                                {sec.title}
                                                            </Box>
                                                        )}
                                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                            <Box className="update-new-feature-content w-100" sx={newFeatureBlockSx} dangerouslySetInnerHTML={{ __html: sec.content || '' }} />
                                                        </Collapse>
                                                    </Box>
                                                )
                                            })
                                        ) : (
                                            <Box className="update-new-feature-content w-100" sx={newFeatureBlockSx} dangerouslySetInnerHTML={{ __html: html }} />
                                        )}
                                    </UseStyle.ShowContent>
                                </div>
                            )
                        })}

                        {updatepage === 'default' &&
                            <div className='d-flex align-items-center justify-content-center'>
                                <UseStyle.ShowContent>
                                    <div className='d-flex justify-content-center'>
                                        <Imgs.DefaultUpdate />
                                    </div>
                                    <div className='w-100 d-flex align-items-center justify-content-center flex-column mt-2'>
                                        <UseStyle.HeaderDetail> <span className='pr-2'><Icons.Noti /></span> {t('UPDATEPAGE.DETAIL.NO_HAVE_NOTI')}</UseStyle.HeaderDetail>
                                        <UseStyle.SubDetail>{t('UPDATEPAGE.DETAIL.NO_HAVE_NOTI_DETAIL')}</UseStyle.SubDetail>
                                    </div>
                                </UseStyle.ShowContent>
                            </div>
                        }
                    </div>
                } ></CollapseContainer>}

            {menu === "Manual" && <CollapseContainer
                tooltipExpand={t('UPDATEPAGE.TOOLTIP.EXPAND_MENU')}
                tooltipShorten={t('UPDATEPAGE.TOOLTIP.SHORTEN_MENU')}
                styleComponentLeft={{ height: '100%', overflowY: 'auto' }}
                styleComponentRight={{ height: '100%', overflowY: 'auto' }}
                callBackStatusCollapse={() => setCollapse(!collapse)}
                componentLeft={
                    <ManualNav
                        selectedManualId={selectedManualId}
                        onSelectManual={setSelectedManualId}
                    />
                }
                componentRight={
                    <div>
                        {manualLoading ? (
                            <div className='d-flex align-items-center justify-content-center' style={{ paddingTop: 64 }}>
                                <CircularProgress size={32} sx={{ color: colors.themeSecondColor }} />
                            </div>
                        ) : loadedManual ? (
                            <ManualContent manual={loadedManual} />
                        ) : (
                            <div className='d-flex align-items-center justify-content-center'>
                                <UseStyle.ShowContent>
                                    <div className='d-flex justify-content-center'>
                                        <Imgs.DefaultUpdate />
                                    </div>
                                    <div className='w-100 d-flex align-items-center justify-content-center flex-column mt-2'>
                                        <UseStyle.HeaderDetail>
                                            <span className='pr-2'><Icons.Manual /></span>
                                            {t('UPDATEPAGE.BUTTON.MANUAL')}
                                        </UseStyle.HeaderDetail>
                                        <UseStyle.SubDetail>เลือกหัวข้อคู่มือจากเมนูด้านซ้าย</UseStyle.SubDetail>
                                    </div>
                                </UseStyle.ShowContent>
                            </div>
                        )}
                    </div>
                }
            />}
            </div>
            </div>
        </div>
    )
}